import { studentApi } from '../services/studentApi';
import abacusService, { AbacusTopicDetailsDTO } from '../services/abacusService';
import { isAuthenticatedApiPath, mayStoreInCache } from './abacusCachePolicy';

const DB_NAME = 'studystride-abacus-offline';
const DB_VERSION = 1;
const CACHE_NAME = 'studystride-abacus-v1';

export interface BundleResource {
  resourceId: number;
  type?: string;
  title?: string;
  url?: string;
  learnerReady?: boolean;
}

export interface BundleQuestion {
  questionId?: string;
  id?: string;
  questionType?: string;
  type?: string;
  prompt?: string;
  promptSpoken?: string;
  content?: string;
  answer?: number;
  correctAnswer?: number;
}

export interface LevelBundle {
  levelCode: string;
  courseId: number;
  enrollmentId: number;
  topics: Array<{
    topicId: number;
    title: string;
    contentMode: string;
    category: string;
    theoryContent?: string | null;
    interactive?: AbacusTopicDetailsDTO['interactive'];
    resources?: BundleResource[];
  }>;
  practicePools?: Array<{
    topicId: number;
    skillTags?: string[];
    questions?: BundleQuestion[];
  }>;
  testWeekPools?: Array<{
    topicId: number;
    poolId?: string;
    version?: number;
    questionsJson?: string;
    questionsHash?: string;
  }>;
}

export interface StoredTopic {
  topicId: number;
  title: string;
  contentMode: string;
  category: string;
  theoryContent: string | null;
  interactive?: AbacusTopicDetailsDTO['interactive'];
  resources: BundleResource[];
  practiceQuestions: BundleQuestion[];
  testWeekQuestions: BundleQuestion[];
  isTestWeek: boolean;
  enrollmentId: number;
}

interface StoredBundle {
  courseId: number;
  levelCode: string;
  enrollmentId: number;
  downloadedAt: string;
  topics: StoredTopic[];
}

export interface QueuedAttempt {
  clientAttemptId: string;
  courseId: number;
  topicId: number;
  enrollmentId: number;
  answers: Array<{
    questionId: string;
    promptSpoken: string;
    expectedAnswer: number;
    answerRaw: string;
    inputMethod: 'VOICE' | 'TYPED';
    sttConfidence?: number | null;
    tolerance?: number;
  }>;
  synced: boolean;
}

export interface SyncStatus {
  clientAttemptId: string;
  attemptId: number;
  score: number;
  duplicate: boolean;
  unlocksLevel: boolean;
}

export class OfflineQuotaError extends Error {
  oldestLevelCode: string | null;

  constructor(oldestLevelCode: string | null) {
    super('Storage is full. Remove the oldest downloaded level.');
    this.name = 'OfflineQuotaError';
    this.oldestLevelCode = oldestLevelCode;
  }
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is unavailable'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('bundles')) db.createObjectStore('bundles');
      if (!db.objectStoreNames.contains('assets')) db.createObjectStore('assets');
      if (!db.objectStoreNames.contains('queue')) db.createObjectStore('queue');
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function idbRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function isQuotaError(error: unknown): boolean {
  return error instanceof DOMException
    && (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED');
}

async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function bundleKey(courseId: number, levelCode: string): string {
  return `${courseId}:${levelCode}`;
}

async function listBundles(): Promise<StoredBundle[]> {
  const db = await openDb();
  const rows = await idbRequest(db.transaction('bundles').objectStore('bundles').getAll());
  return (rows as StoredBundle[]) ?? [];
}

export async function oldestDownloadedLevel(courseId: number): Promise<string | null> {
  const rows = (await listBundles()).filter((row) => row.courseId === courseId);
  if (rows.length === 0) return null;
  rows.sort((a, b) => a.downloadedAt.localeCompare(b.downloadedAt));
  return rows[0].levelCode;
}

export async function removeDownloadedLevel(courseId: number, levelCode: string): Promise<void> {
  const db = await openDb();
  await idbRequest(db.transaction('bundles', 'readwrite').objectStore('bundles').delete(bundleKey(courseId, levelCode)));
}

export async function readCachedTopic(courseId: number, topicId: number): Promise<StoredTopic | null> {
  const rows = (await listBundles()).filter((row) => row.courseId === courseId);
  for (const row of rows) {
    const topic = row.topics.find((item) => item.topicId === topicId);
    if (topic) {
      return { ...topic, enrollmentId: row.enrollmentId };
    }
  }
  return null;
}

async function saveBundle(record: StoredBundle): Promise<void> {
  const db = await openDb();
  await idbRequest(
    db.transaction('bundles', 'readwrite').objectStore('bundles').put(record, bundleKey(record.courseId, record.levelCode))
  );
}

async function storeExplicitFile(url: string): Promise<void> {
  const absolute = new URL(url, window.location.origin);
  if (!mayStoreInCache(absolute.toString())) {
    if (!isAuthenticatedApiPath(absolute.pathname)) return;
    const response = await studentApi.get(absolute.pathname + absolute.search, { responseType: 'blob' });
    const db = await openDb();
    await idbRequest(
      db.transaction('assets', 'readwrite').objectStore('assets').put(response.data, absolute.toString())
    );
    return;
  }
  if (!('caches' in window)) return;
  const cache = await caches.open(CACHE_NAME);
  await cache.add(new Request(absolute.toString(), { credentials: 'omit' }));
}

function parseQuestions(json: string | undefined): BundleQuestion[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json) as BundleQuestion[];
    return Array.isArray(parsed) ? parsed.slice(0, 1) : [];
  } catch {
    return [];
  }
}

export async function downloadLevelBundle(
  courseId: number,
  levelCode: string,
  onProgress: (percent: number) => void
): Promise<void> {
  onProgress(5);
  const bundle = await abacusService.fetchLevelBundle(courseId, levelCode);
  const verifiedTestWeeks = new Map<number, BundleQuestion[]>();
  for (const pool of bundle.testWeekPools ?? []) {
    const actual = await sha256Hex(pool.questionsJson ?? '');
    if (pool.questionsHash && actual === pool.questionsHash) {
      verifiedTestWeeks.set(pool.topicId, parseQuestions(pool.questionsJson));
    }
  }
  const topics: StoredTopic[] = (bundle.topics ?? []).map((topic) => {
    const practice = (bundle.practicePools ?? []).find((pool) => pool.topicId === topic.topicId);
    return {
      topicId: topic.topicId,
      title: topic.title,
      contentMode: topic.contentMode,
      category: topic.category,
      theoryContent: topic.theoryContent ?? null,
      interactive: topic.interactive,
      resources: topic.resources ?? [],
      practiceQuestions: (practice?.questions ?? []).slice(0, 1).map((question) => ({
        ...question,
        questionType: question.questionType ?? 'MENTAL_ARITHMETIC',
      })),
      testWeekQuestions: verifiedTestWeeks.get(topic.topicId) ?? [],
      isTestWeek: verifiedTestWeeks.has(topic.topicId),
      enrollmentId: bundle.enrollmentId,
    };
  });
  const files = topics.flatMap((topic) =>
    topic.resources.filter((resource) => resource.learnerReady && resource.url)
  );
  const total = files.length + 1;
  let done = 0;
  const tick = () => {
    done += 1;
    onProgress(Math.min(100, Math.round((done / total) * 100)));
  };
  try {
    for (const file of files) {
        try {
          await storeExplicitFile(file.url as string);
        } catch (error) {
          if (isQuotaError(error)) throw error;
        }
        tick();
    }
    await saveBundle({
      courseId,
      levelCode,
      enrollmentId: bundle.enrollmentId,
      downloadedAt: new Date().toISOString(),
      topics,
    });
    tick();
  } catch (error) {
    if (isQuotaError(error)) {
      throw new OfflineQuotaError(await oldestDownloadedLevel(courseId).catch(() => null));
    }
    throw error;
  }
  onProgress(100);
}

export async function enqueueAttempt(attempt: QueuedAttempt): Promise<void> {
  const db = await openDb();
  await idbRequest(db.transaction('queue', 'readwrite').objectStore('queue').put(attempt, attempt.clientAttemptId));
}

let flushing = false;

export async function flushQueuedAttempts(): Promise<SyncStatus | null> {
  if (flushing || typeof navigator !== 'undefined' && !navigator.onLine) return null;
  flushing = true;
  try {
    const db = await openDb();
    const rows = await idbRequest(db.transaction('queue').objectStore('queue').getAll()) as QueuedAttempt[];
    const pending = (rows ?? []).filter((row) => row && !row.synced);
    if (pending.length === 0) return null;
    const body = {
      attempts: pending.map((row) => ({
        clientAttemptId: row.clientAttemptId,
        topicId: row.topicId,
        enrollmentId: row.enrollmentId,
        answers: row.answers,
      })),
    };
    sessionStorage.setItem('abacus-last-sync-body', JSON.stringify(body));
    const response = await abacusService.syncPractice(body);
    const writer = db.transaction('queue', 'readwrite').objectStore('queue');
    await Promise.all(pending.map((row) => idbRequest(writer.put({ ...row, synced: true }, row.clientAttemptId))));
    const first = response.results?.[0];
    if (!first) return null;
    const status: SyncStatus = {
      clientAttemptId: first.clientAttemptId,
      attemptId: first.attemptId,
      score: Number(first.score),
      duplicate: Boolean(first.duplicate),
      unlocksLevel: false,
    };
    sessionStorage.setItem('abacus-sync-status', JSON.stringify(status));
    return status;
  } finally {
    flushing = false;
  }
}
